/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Link as ReactRouterLink } from 'react-router';

import classNames from 'classnames';

import type { LinkProps, Theme } from '@coursera/cds-core';
import { Link, Typography2, useTheme } from '@coursera/cds-core';
import { PlayIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import TogglableContent from 'bundles/page/components/TogglableContent';
import ToggleButton from 'bundles/video-logged-out-page/components/ToggleButton';
import type { VLPItem, VLPModule } from 'bundles/video-logged-out-page/types/vlpSharedTypes';
import { millisToMinutesAndSeconds } from 'bundles/video-logged-out-page/utils/utils';

import _t from 'i18n!nls/video-logged-out-page';

const MAX_DESC_CHARS = 260;
const COLLAPSED_DESC_HEIGHT = 92;

type Props = {
  currentModule: VLPModule;
  lessonLectureItems: Array<VLPItem>;
  itemId: string;
  courseSlug: string;
};

const styles = {
  lessonContainer: (theme: Theme) =>
    css({
      padding: '1em 2em',
      border: `1px solid var(--cds-color-grey-50)`,
      borderLeft: `6px solid var(--cds-color-grey-50)`,
    }),
  fromTheLesson: css({
    paddingTop: '6px',
    paddingBottom: '12px',
  }),
  moduleName: (theme: Theme) =>
    css({
      marginBottom: 'var(--cds-spacing-100)',
    }),
  lessonDescription: (theme: Theme) =>
    css({
      paddingBottom: 0,
    }),
  otherVideos: css`
    margin-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-300);

    .current-video {
      .other-video-link-cds {
        background-color: #f0f0f0;
      }
    }
  `,
  videoLinkWrapperAndContent: css`
    margin-bottom: var(--cds-spacing-150);

    .other-video-link-cds {
      display: 'flex';
      padding: '2px 0';
      align-items: 'center';
    }

    .video-name {
      margin-right: var(--cds-spacing-100);
      color: var(--cds-color-blue-700);
    }
  `,
};

const TrackedLink = withSingleTracked({ type: 'BUTTON' })<LinkProps<ReactRouterLink>>(
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  Link
);

export const LessonLectures: React.FC<Props> = ({ currentModule, lessonLectureItems, itemId, courseSlug }) => {
  const theme = useTheme();
  const descTooLong = (currentModule?.description || '').length > MAX_DESC_CHARS;

  return (
    <div className="rc-LessonLectures vlp-container-section" css={styles.lessonContainer(theme)}>
      <Typography2 component="p" color="supportText" variant="bodySecondary" css={styles.fromTheLesson}>
        {_t('From the lesson')}
      </Typography2>
      {currentModule && (
        <Typography2 component="p" variant="bodyPrimary" css={styles.moduleName(theme)}>
          {currentModule.name}
        </Typography2>
      )}
      {currentModule?.description &&
        (descTooLong ? (
          <TogglableContent
            trackingName="moduleDescriptionBody"
            toggleButtonType="link"
            collapsedHeight={COLLAPSED_DESC_HEIGHT}
            renderToggleButtonContentFunction={ToggleButton}
          >
            <Typography2
              component="p"
              css={styles.lessonDescription(theme)}
              className="lesson-description"
              data-e2e="lesson-description"
              variant="bodySecondary"
            >
              {currentModule.description}
            </Typography2>
          </TogglableContent>
        ) : (
          <Typography2
            component="p"
            css={styles.lessonDescription(theme)}
            className="lesson-description"
            data-e2e="lesson-description"
            variant="bodySecondary"
          >
            {currentModule.description}
          </Typography2>
        ))}
      <div className="other-videos" css={styles.otherVideos}>
        {lessonLectureItems.map((item) => (
          <div
            css={styles.videoLinkWrapperAndContent}
            className={classNames('video-link-wrapper', { 'current-video': item.id === itemId })}
            key={`${item.slug}`}
          >
            <TrackedLink
              {...Link.defaultProps}
              component={ReactRouterLink}
              variant="quiet"
              className="other-video-link-cds"
              href={`/lecture/${courseSlug}/${item.slug}-${item.id}`}
              trackingName="other_videos_link"
              icon={<PlayIcon size="small" color="interactive" className="play-icon" name="play-circle" />}
              iconPosition="before"
            >
              <Typography2 component="span" className="video-name">
                {item.name}
              </Typography2>
              <Typography2 variant="bodySecondary" component="span" className="video-duration">
                {millisToMinutesAndSeconds(item.timeCommitment)}
              </Typography2>
            </TrackedLink>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LessonLectures;
